import { useState, useRef, useEffect, useCallback } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";

interface YouTubePlayer {
  mute: () => void;
  unMute: () => void;
  setVolume: (volume: number) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  getCurrentTime: () => number;
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void;
  destroy: () => void;
}


interface YouTubePlayerConstructor {
  new (
    element: HTMLDivElement,
    options: {
      videoId: string;
      playerVars: Record<string, number | string>;
      events: {
        onReady: () => void;
        onStateChange?: (event: { data: number }) => void;
      };
    }
  ): YouTubePlayer;
}

interface YouTubeAPI {
  Player: YouTubePlayerConstructor;
}

declare global {
  interface Window {
    YT: YouTubeAPI;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface LazyYouTubeEmbedProps {
  videoId: string;
  title: string;
  className?: string;
  thumbnailQuality?: "default" | "hqdefault" | "mqdefault" | "sddefault" | "maxresdefault";
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  showCaptions?: boolean;
  alwaysShowSound?: boolean;
}


// Load YouTube IFrame API once
let apiLoaded = false;
let apiReady = false;
const apiReadyCallbacks: (() => void)[] = [];

const loadYouTubeAPI = () => {
  if (apiLoaded) return;
  apiLoaded = true;
  
  const tag = document.createElement("script");
  tag.src = "https://www.youtube.com/iframe_api";
  const firstScriptTag = document.getElementsByTagName("script")[0];
  firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
  
  window.onYouTubeIframeAPIReady = () => {
    apiReady = true;
    apiReadyCallbacks.forEach(cb => cb());
    apiReadyCallbacks.length = 0;
  };
};

const onAPIReady = (callback: () => void) => {
  if (apiReady) {
    callback();
  } else {
    apiReadyCallbacks.push(callback);
  }
};

const LazyYouTubeEmbed = ({
  videoId,
  title,
  className = "",
  thumbnailQuality = "maxresdefault",
  autoplay = false,
  muted: initialMuted = true,
  loop = true,
  showCaptions = false,
  alwaysShowSound = false,

}: LazyYouTubeEmbedProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isMuted, setIsMuted] = useState(initialMuted);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const pausedTimeRef = useRef(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);


  // Lazy load when in viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px", threshold: 0 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-load if autoplay is enabled and in view
  useEffect(() => {
    if (isInView && autoplay) {
      const timer = setTimeout(() => setIsLoaded(true), 500);
      return () => clearTimeout(timer);
    }
  }, [isInView, autoplay]);

  const captionTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Force la désactivation des sous-titres (auto-générés inclus)
  const killCaptions = useCallback(() => {
    if (showCaptions) return;
    try {
      const p = playerRef.current as unknown as {
        unloadModule?: (m: string) => void;
        setOption?: (m: string, o: string, v: unknown) => void;
      };
      if (!p) return;
      p.setOption?.("captions", "track", {});
      p.setOption?.("cc", "track", {});
      p.setOption?.("captions", "reload", false);
      p.unloadModule?.("captions");
      p.unloadModule?.("cc");
    } catch {
      /* noop */
    }
  }, [showCaptions]);

  useEffect(() => {
    return () => {
      captionTimersRef.current.forEach(clearTimeout);
      captionTimersRef.current = [];
    };
  }, []);

  // Initialize YouTube player

  useEffect(() => {
    if (!isLoaded || !playerContainerRef.current) return;
    
    loadYouTubeAPI();
    
    onAPIReady(() => {
      if (playerRef.current) return;
      
      playerRef.current = new window.YT.Player(playerContainerRef.current!, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          loop: loop ? 1 : 0,
          playlist: videoId,
          controls: 0,
          showinfo: 0,
          rel: 0,
          modestbranding: 1,
          cc_load_policy: showCaptions ? 1 : 0,
          cc_lang_pref: "fr",
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
          playsinline: 1,
          annotations: 0,
        },
        events: {
          onReady: () => {
            setPlayerReady(true);
            killCaptions();
            // Relance plusieurs fois: YouTube recharge parfois la piste auto
            const stamps = [0, 300, 800, 1500, 3000, 5000, 8000];
            stamps.forEach((ms) => {
              const t = setTimeout(killCaptions, ms);
              captionTimersRef.current.push(t);
            });
            // Démarre muet (autoplay fiable), puis tente le son
            playerRef.current?.playVideo();
            if (!initialMuted) {
              setTimeout(() => {
                try {
                  playerRef.current?.unMute();
                  playerRef.current?.setVolume(100);
                  setIsMuted(false);
                } catch {
                  /* noop */
                }
              }, 400);
            }
          },
          onStateChange: (event) => {
            killCaptions();
            if (event.data === 1) {
              setIsPlaying(true);
              setHasStarted(true);
            }
            if (event.data === 2 || event.data === 0) setIsPlaying(false);
          },



        },
      });
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [isLoaded, videoId, initialMuted, loop, killCaptions]);

  const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/${thumbnailQuality}.jpg`;

  const handleClick = () => {
    setIsLoaded(true);
  };

  const toggleMute = useCallback(() => {
    if (!playerRef.current || !playerReady) return;
    
    if (isMuted) {
      playerRef.current.unMute();
      playerRef.current.setVolume(100);
      setIsMuted(false);
    } else {
      playerRef.current.mute();
      setIsMuted(true);
    }
  }, [isMuted, playerReady]);

  const togglePlay = useCallback(() => {
    const player = playerRef.current;
    if (!player || !playerReady) return;

    if (isPlaying) {
      try {
        pausedTimeRef.current = player.getCurrentTime();
      } catch {
        /* noop */
      }
      player.pauseVideo();
    } else {
      try {
        if (pausedTimeRef.current > 0) {
          player.seekTo(pausedTimeRef.current, true);
        }
      } catch {
        /* noop */
      }
      player.playVideo();
    }
  }, [isPlaying, playerReady]);


  return (
    <div
      ref={containerRef}
      className={`group relative overflow-hidden ${className}`}
      style={{ aspectRatio: "16/9" }}
    >
      {!isLoaded ? (
        <button
          onClick={handleClick}
          className="w-full h-full relative group cursor-pointer"
          aria-label={`Play video: ${title}`}
        >
          {/* Thumbnail */}
          {isInView && (
            <img
              src={thumbnailUrl}
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          )}
          
          {/* Placeholder before image loads */}
          {!isInView && (
            <div className="w-full h-full bg-muted animate-pulse" />
          )}
          
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-primary/30">
              <Play className="w-7 h-7 md:w-9 md:h-9 text-white fill-white ml-1" />
            </div>
          </div>
        </button>
      ) : (
        <>
          <div
            ref={playerContainerRef}
            className={`w-full h-full ${hasStarted ? "" : "pointer-events-none"}`}
          />

          {/* Voile de démarrage : cache l'UI YouTube tant que la vidéo n'a pas commencé */}
          {!hasStarted && (
            <button
              type="button"
              onClick={togglePlay}
              className="absolute inset-0 z-[5]"
              aria-label="Lancer la vidéo"
            >
              <span
                className="absolute inset-0"
                style={{ backgroundColor: "hsl(var(--background))" }}
              >
                <img
                  src={thumbnailUrl}
                  alt={title}
                  className="w-full h-full object-cover opacity-70"
                  loading="lazy"
                  decoding="async"
                />
              </span>
            </button>
          )}

          {/* Zone de clic invisible sur toute la vidéo : play/pause, bloque les gestes YouTube */}
          {hasStarted && (
            <button
              type="button"
              onClick={togglePlay}
              className="absolute inset-0 z-[4]"
              aria-label={isPlaying ? "Mettre la vidéo en pause" : "Reprendre la vidéo"}
            />
          )}



          {/* Sound toggle button */}
          <button
            onClick={toggleMute}
            className={`absolute bottom-4 right-4 z-10 p-3 rounded-full bg-black/60 hover:bg-black/80 text-white transition-opacity duration-150 backdrop-blur-sm ${alwaysShowSound ? "" : "[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"}`}
            aria-label={isMuted ? "Activer le son" : "Couper le son"}
          >
            {isMuted ? (
              <VolumeX className="w-5 h-5" />
            ) : (
              <Volume2 className="w-5 h-5" />
            )}
          </button>
        </>
      )}
    </div>
  );
};

export default LazyYouTubeEmbed;
